import React, { useState } from 'react'

import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import Icon from 'components_new/atoms/Icon'
import Text from 'components_new/atoms/Text'

import SimpleDialog from 'components_new/molecules/SimpleDialog'

import { useDataRunsActions } from 'redux/actions/internal/DataRuns'
import { useInternalDataRuns } from 'redux/hooks/internal/DataRuns'
import { JobSummary } from 'redux/selectors/internal/DataRuns'

interface StartJobActionProps {
  job: JobSummary
  jobs: JobSummary[]
}

/** "Starta jobbet" with its confirmation, for one job in the panel. */
const StartJobAction = (props: StartJobActionProps) => {
  const { job, jobs } = props

  const { startingJobNames } = useInternalDataRuns()
  const { tryStartDataRunJob } = useDataRunsActions()

  const [confirmOpen, setConfirmOpen] = useState(false)

  // The job as of the latest fetch.
  const liveJob =
    jobs.find(
      (candidate) =>
        candidate.job_name === job.job_name &&
        candidate.customer?.id === job.customer?.id
    ) ?? job

  const isRunning = liveJob.status === 'running'
  const isStarting = startingJobNames.includes(job.job_name)
  const customerName = job.customer?.name ?? 'Okänd kund'

  const handleConfirm = () => {
    setConfirmOpen(false)
    tryStartDataRunJob(job.job_name)
  }

  return (
    <Box>
      <Text sx={{ fontWeight: 700 }} variant="subtitle2">
        Åtgärder
      </Text>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
        <Button
          color="black"
          disabled={isRunning}
          fullWidth
          loading={isStarting}
          loadingTitle="Startar"
          onClick={() => setConfirmOpen(true)}
          startIcon={<Icon fontSize="small" name="StartJob" />}
          variant="outlined"
        >
          Starta jobbet
        </Button>
        {isRunning ? (
          <Text sx={{ color: 'text.disabled' }} variant="caption">
            Jobbet kör just nu och kan startas när körningen är klar.
          </Text>
        ) : null}
      </Box>

      <SimpleDialog
        actions={
          <>
            <Button onClick={() => setConfirmOpen(false)} variant="text">
              Avbryt
            </Button>
            <Button onClick={handleConfirm} variant="contained">
              Starta
            </Button>
          </>
        }
        maxWidth="xs"
        onClose={() => setConfirmOpen(false)}
        open={confirmOpen}
        title="Starta jobbet?"
      >
        <Text sx={{ wordBreak: 'break-word' }} variant="body2">
          <b>{job.job_name}</b> startas för {customerName} i AWS Glue, utan att
          något annat körs först.
        </Text>
        <Text sx={{ mt: 1.5 }} variant="body2">
          Jobben som följer efter startar själva när det är klart, precis som
          vid en vanlig körning.
        </Text>
      </SimpleDialog>
    </Box>
  )
}

export default StartJobAction
